<template>
  <div class="tcla">
    <!-- Header -->
    <div class="tcla-header">
      <div class="tcla-header__top">
        <h2 class="tcla-header__title">🧠 تحلیل لینک داخلی و اقتدار موضوعی</h2>
        <span class="tcla-badge" :class="overallBadgeClass">{{ overallStatusText }}</span>
      </div>
      <p class="tcla-header__subtitle">تجزیه معنایی لینک‌های داخلی | شناسایی Hub/Spoke | شکاف‌های محتوایی</p>
    </div>

    <!-- Quick Stats -->
    <div class="tcla-stats">
      <div class="tcla-stat">
        <span class="tcla-stat__label">صفحات تجزیه‌شده</span>
        <span class="tcla-stat__value">{{ seoAnalysis.profiles.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">خوشه‌های موضوعی</span>
        <span class="tcla-stat__value">{{ seoAnalysis.topicalClusters.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">صفحات Hub</span>
        <span class="tcla-stat__value">{{ seoAnalysis.hubs.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">فرصت لینک‌سازی</span>
        <span class="tcla-stat__value">{{ seoAnalysis.missingLinks.length }}</span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tcla-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tcla-tab"
        :class="{ 'tcla-tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.icon }} {{ tab.label }}
        <span v-if="tab.badge" class="tcla-tab__badge">{{ tab.badge }}</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tcla-tab-content">
      <TCLADashboard
        v-if="activeTab === 'dashboard'"
        :analysis="seoAnalysis"
        @edit-blog="handleEditBlog"
      />
      <TCLAHubSpoke
        v-if="activeTab === 'hubspoke'"
        :hubs="seoAnalysis.hubs"
        :spokes="seoAnalysis.spokes"
        :graph="seoAnalysis.graph"
        @edit-blog="handleEditBlog"
      />
      <TCLAOrphans
        v-if="activeTab === 'orphans'"
        :orphans="seoAnalysis.orphans"
        :dead-ends="seoAnalysis.deadEnds"
        @edit-blog="handleEditBlog"
      />
      <TCLAAnchors
        v-if="activeTab === 'anchors'"
        :anchor-analysis="seoAnalysis.anchorAnalysis"
        @edit-blog="handleEditBlog"
      />
      <TCLAOpportunities
        v-if="activeTab === 'opportunities'"
        :missing-links="seoAnalysis.missingLinks"
        :graph="seoAnalysis.graph"
        @edit-blog="handleEditBlog"
      />
      <TCLAGaps
        v-if="activeTab === 'gaps'"
        :content-structure="seoAnalysis.exportJSON.contentStructure"
        @copy="copyText"
      />
      <TCLAActionPlan
        v-if="activeTab === 'actionplan'"
        :content-plan="seoAnalysis.actionPlan.contentPlan"
        :link-building-plan="seoAnalysis.actionPlan.linkBuildingPlan"
        @edit-blog="handleEditBlog"
      />
      <TCLAArticleReport
        v-if="activeTab === 'articles'"
        :articles="seoAnalysis.articleReports"
        @edit-blog="handleEditBlog"
        @copy-json="copyExportJSON"
        @download-json="downloadExportJSON"
      />
      <TCLAIrrelevantLinks
        v-if="activeTab === 'irrelevant'"
        :irrelevant-links="seoAnalysis.irrelevantLinks"
        :graph="seoAnalysis.graph"
        @edit-blog="handleEditBlog"
      />
    </div>
  </div>
</template>

<script>
import SeoEngine from './SeoEngine.js';
import TCLADashboard from './TCLADashboard.vue';
import TCLAHubSpoke from './TCLAHubSpoke.vue';
import TCLAOrphans from './TCLAOrphans.vue';
import TCLAAnchors from './TCLAAnchors.vue';
import TCLAOpportunities from './TCLAOpportunities.vue';
import TCLAGaps from './TCLAGaps.vue';
import TCLAActionPlan from './TCLAActionPlan.vue';
import TCLAArticleReport from './TCLAArticleReport.vue';
import TCLAIrrelevantLinks from './TCLAIrrelevantLinks.vue';

export default {
  name: 'TopicClusterLinkAnalysis',
  components: {
    TCLADashboard,
    TCLAHubSpoke,
    TCLAOrphans,
    TCLAAnchors,
    TCLAOpportunities,
    TCLAGaps,
    TCLAActionPlan,
    TCLAArticleReport,
    TCLAIrrelevantLinks
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'و', 'یا', 'که',
        'این', 'آن', 'ما', 'شما', 'او', 'یک', 'دو', 'بدون', 'اما'
      ]
    },
    // v2.0 config (all optional)
    locations: { type: Array, default: null },
    industries: { type: Array, default: null },
    clusterMap: { type: Array, default: null },
    gapDictionary: { type: Array, default: null },
    familyThreshold: { type: Number, default: 55 }
  },
  data() {
    return {
      activeTab: 'actionplan'
    };
  },
  computed: {
    seoEngineConfig() {
      return {
        locations: this.locations || undefined,
        industries: this.industries || undefined,
        clusterMap: this.clusterMap || undefined,
        gapDictionary: this.gapDictionary || undefined,
        familyThreshold: this.familyThreshold
      };
    },
    seoAnalysis() {
      if (!this.items || this.items.length === 0) {
        return {
          profiles: [],
          graph: { nodes: {}, urlSet: new Set() },
          pageRank: { raw: {}, normalized: {} },
          topicalClusters: [],
          hubs: [],
          spokes: [],
          orphans: [],
          deadEnds: [],
          anchorAnalysis: [],
          missingLinks: [],
          irrelevantLinks: [],
          keywordGaps: [],
          pageScores: {},
          actionPlan: { contentPlan: [], linkBuildingPlan: [] },
          articleReports: [],
          exportJSON: { generatedAt: null, summary: {}, topicalClusters: [], keywordGaps: [], linkBuildingPlan: [], irrelevantLinks: [], contentStructure: [], articles: [] }
        };
      }
      return SeoEngine.analyze(this.items, this.stopWords, this.seoEngineConfig);
    },
    tabs() {
      return [
        { key: 'actionplan', icon: '🤖', label: 'دستیار سئو', badge: this.seoAnalysis.actionPlan.contentPlan.length },
        { key: 'articles', icon: '📄', label: 'گزارش مقالات', badge: this.seoAnalysis.articleReports.length },
        { key: 'dashboard', icon: '📊', label: 'داشبورد', badge: null },
        { key: 'hubspoke', icon: '🧭', label: 'Hub/Spoke', badge: this.seoAnalysis.hubs.length + this.seoAnalysis.spokes.length },
        { key: 'orphans', icon: '🏝️', label: 'یتیم/بن‌بست', badge: this.seoAnalysis.orphans.length + this.seoAnalysis.deadEnds.length },
        { key: 'irrelevant', icon: '🚫', label: 'لینک‌های بی‌ربط', badge: this.seoAnalysis.irrelevantLinks.length },
        { key: 'anchors', icon: '⚓', label: 'انکرتکست', badge: null },
        { key: 'opportunities', icon: '💡', label: 'فرصت‌ها', badge: this.seoAnalysis.missingLinks.length },
        { key: 'gaps', icon: '🧠', label: 'تحلیل با AI', badge: null }
      ];
    },
    overallBadgeClass() {
      const orphanCount = this.seoAnalysis.orphans.length;
      const deadEndCount = this.seoAnalysis.deadEnds.length;
      if (orphanCount > 0 || deadEndCount > 0) return 'tcla-badge--red';
      return 'tcla-badge--green';
    },
    overallStatusText() {
      const orphans = this.seoAnalysis.orphans.length;
      const deadEnds = this.seoAnalysis.deadEnds.length;
      if (orphans > 0) return `${orphans} یتیم`;
      if (deadEnds > 0) return `${deadEnds} بن‌بست`;
      return '✅ سالم';
    }
  },
  methods: {
    handleEditBlog(source) {
      window.open("/dashboard/news/edit-news/"+source)
      // this.$emit('edit-blog', source);
    },
    copyText(text, msg) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(msg || 'کپی شد');
    },
    copyExportJSON() {
      const jsonStr = JSON.stringify(this.seoAnalysis.exportJSON, null, 2);
      this.copyText(jsonStr, 'گزارش JSON کپی شد — می‌توانید آن را برای بررسی بیشتر ارسال کنید');
    },
    downloadExportJSON() {
      const jsonStr = JSON.stringify(this.seoAnalysis.exportJSON, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seo-report-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
:root {
  --c-text: #1f2937;
  --c-text-secondary: #6b7280;
  --c-text-muted: #9ca3af;
  --c-surface: #ffffff;
  --c-surface-alt: #f9fafb;
  --c-border-light: #e5e7eb;
  --c-border: #d1d5db;
  --c-accent: #3b82f6;
  --c-accent-light: #dbeafe;
  --c-success: #16a34a;
  --c-warning: #f59e0b;
  --c-danger: #ef4444;
  --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --shadow-md: 0 4px 6px rgba(0,0,0,0.1);
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
}

.tcla {
  font-family: 'Vazirmatn', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  direction: rtl;
  padding: 24px;
  background: var(--c-surface-alt);
}

.tcla-header {
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm);
}

.tcla-header__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tcla-header__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--c-text);
  margin: 0;
}

.tcla-header__subtitle {
  font-size: 0.85rem;
  color: var(--c-text-secondary);
  margin: 0;
}

.tcla-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
}

.tcla-badge--green { background: var(--c-success); }
.tcla-badge--red { background: var(--c-danger); }

.tcla-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.tcla-stat {
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-md);
  padding: 16px;
  text-align: center;
  box-shadow: var(--shadow-sm);
}

.tcla-stat__label {
  display: block;
  font-size: 0.75rem;
  color: var(--c-text-muted);
  margin-bottom: 6px;
  font-weight: 500;
}

.tcla-stat__value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--c-accent);
}

.tcla-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  padding: 6px;
  margin-bottom: 20px;
}

.tcla-tab {
  flex: 0 1 auto;
  padding: 9px 14px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--c-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  position: relative;
}

.tcla-tab:hover { background: var(--c-surface-alt); color: var(--c-text); }
.tcla-tab--active {
  background: var(--c-accent);
  border : 1px solid blue;
  color: blue; }

.tcla-tab__badge {
  display: inline-block;
  background: rgba(255,255,255,0.3);
  border-radius: 100px;
  padding: 1px 6px;
  font-size: 0.65rem;
  margin-right: 4px;
}

.tcla-tab:not(.tcla-tab--active) .tcla-tab__badge {
  background: var(--c-accent-light);
  color: var(--c-accent);
}

.tcla-tab-content {
  min-height: 300px;
}

.tcla-panel {
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 16px;
}

@media (max-width: 900px) {
  .tcla-stats { grid-template-columns: repeat(2, 1fr); }
  .tcla-tabs { flex-wrap: wrap; }
}

@media (max-width: 600px) {
  .tcla { padding: 16px; }
  .tcla-stats { grid-template-columns: 1fr 1fr; }
  .tcla-header__top { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
